import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { axiosPrivate } from 'app/utils';

export type FeatureFlagKey = 'webBuilderAccessEnabled' | 'hasBespoke';

export interface OrganisationFeatureFlags {
  webBuilderAccessEnabled: boolean;
  hasBespoke: boolean;
}

export interface GetOrganisationFeatureFlagsResponse {
  organisationId: string;
  featureFlags: OrganisationFeatureFlags;
}

export const organisationFeatureFlagsQueryKey = (organisationId: string) => [
  'organisation-feature-flags',
  organisationId,
];

function getOrganisationFeatureFlags(organisationId: string) {
  return axiosPrivate.get<GetOrganisationFeatureFlagsResponse>(
    `/v3/pharmacist/organisations/${organisationId}/feature-flags`
  );
}

export interface UseOrganisationFeatureFlagsResult {
  featureFlags: OrganisationFeatureFlags | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
}

interface UseOrganisationFeatureFlagsOptions {
  enabled?: boolean;
}

export function useOrganisationFeatureFlags({
  enabled = true,
}: UseOrganisationFeatureFlagsOptions = {}): UseOrganisationFeatureFlagsResult {
  const [searchParams] = useSearchParams();
  const organisationId = searchParams.get('organisationId') ?? '';

  const {
    data,
    isInitialLoading: isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: organisationFeatureFlagsQueryKey(organisationId),
    queryFn: () => getOrganisationFeatureFlags(organisationId),
    enabled: !!organisationId && enabled,
    meta: { errorMessage: 'Failed to load builder settings' },
  });

  return {
    featureFlags: data?.data.featureFlags ?? null,
    isLoading,
    isError,
    refetch,
  };
}
